import React, { useMemo } from 'react';
import { Box, Skeleton, Stack } from '@mui/material';
import classNames from 'classnames';
import ErrorBoundary from 'components/ErrorBoundary';
import ErrorMessage from 'components/ErrorMessage';
import useCustomError from 'hooks/useCustomError';
import type { SunlightTimes } from 'types/sunlightTimes';
import type { SurfData, ObservationType } from 'types/surf';
import type { ConditionRating } from 'types/conditionRating';
import { findMatchingSurfObservation } from 'utils/surfObservationMatch';

import GraphSurfContainer from './components/GraphSurfContainer';
import GraphLegend from './components/GraphLegend';

import styles from './ForecastGraphSurf.module.scss';

interface ForecastGraphSurfContentProps {
  activeItem?: { day: number; hour: number; condition: ConditionRating };
  data: SurfData;
  dataTestId?: string;
  daysToDisplay: number[];
  isMobile: boolean;
  loading?: boolean;
  showTimeline: boolean;
  sunlightTimes: SunlightTimes;
}

const ForecastGraphSurfContent: React.FC<ForecastGraphSurfContentProps> = ({
  activeItem,
  data,
  dataTestId = 'forecast-graph-surf',
  daysToDisplay,
  isMobile,
  loading,
  showTimeline,
  sunlightTimes,
}) => {
  const activeObservation = useMemo(() => {
    if (!activeItem || activeItem.day !== 0 || !data.observations?.length) return null;
    const activeForecast = data.days[0]?.[activeItem.hour];
    if (!activeForecast) return null;

    return findMatchingSurfObservation(data.observations, activeForecast.timestamp)?.surf ?? null;
  }, [activeItem, data.days, data.observations]);

  const observationType = useMemo(() => {
    if (!data?.observations?.length) return null;

    // First check if the active item has an observation with a specific type
    if (activeObservation?.type === 'CAMERA') {
      return 'CAMERA' as ObservationType;
    }
    if (activeObservation?.type === 'HUMAN') {
      return 'HUMAN' as ObservationType;
    }

    // Check if we have both camera and human observations
    const hasCamera = data.observations.some((observation) => observation?.surf?.type === 'CAMERA');
    const hasHuman = data.observations.some((observation) => observation?.surf?.type === 'HUMAN');

    if (hasCamera && hasHuman) {
      return 'MULTIPLE' as ObservationType;
    }

    // Fall back to checking if any observations exist of a certain type
    if (hasCamera) {
      return 'CAMERA' as ObservationType;
    }

    if (hasHuman) {
      return 'HUMAN' as ObservationType;
    }

    return null;
  }, [activeObservation, data?.observations]);

  // Determine if the active item has observation data
  const hasActiveObservation = activeObservation?.max != null && activeObservation.max >= 0;

  if (loading) {
    return (
      <Skeleton
        animation="wave"
        variant="rectangular"
        data-testid={`${dataTestId}-loading`}
        className={styles.skeletonLoader}
      />
    );
  }

  return (
    <Box className={styles.forecastGraphContainer}>
      {daysToDisplay.length === 1 ? (
        <Box className={styles.graphDayContainer} data-testid={dataTestId}>
          <GraphSurfContainer
            activeHour={activeItem?.hour}
            data={data}
            dataTestId={dataTestId}
            day={daysToDisplay[0]}
            isMobile={isMobile}
            key={`key-${daysToDisplay.join()}`}
            showTimeline={showTimeline}
            sunlightTimes={sunlightTimes}
          />
        </Box>
      ) : (
        <Stack className={styles.graphDaysStack} data-testid={dataTestId}>
          {daysToDisplay.map((day, index) => (
            <GraphSurfContainer
              activeHour={day === activeItem?.day ? activeItem?.hour : undefined}
              data={data}
              dataTestId={`${dataTestId}-${day}-container`}
              day={day}
              dayIndex={index}
              isMobile={isMobile}
              key={`key-${daysToDisplay.join()}-${day}`}
              showTimeline={showTimeline}
              sunlightTimes={sunlightTimes}
            />
          ))}
        </Stack>
      )}
      <Box
        className={classNames(styles.legendContainer, {
          [styles.singleDay]: daysToDisplay.length === 1,
          [styles.multipleDays]: daysToDisplay.length !== 1,
        })}
      >
        <GraphLegend
          dataTestId={`${dataTestId}-legend`}
          observationType={observationType}
          hasActiveObservation={hasActiveObservation}
        />
      </Box>
    </Box>
  );
};

export const ForecastGraphSurf: React.FC<ForecastGraphSurfContentProps> = (props) => {
  const { dataTestId, data } = props;
  const { errorMessage, ErrorIcon, hasError } = useCustomError({ error: data?.error });

  return (
    <ErrorBoundary
      error={hasError}
      errorBoundaryId="forecast-graph-surf"
      render={() => (
        <ErrorMessage dataTestId={`${dataTestId}-error`} Icon={ErrorIcon} message={errorMessage} />
      )}
    >
      <ForecastGraphSurfContent {...props} />
    </ErrorBoundary>
  );
};

export default ForecastGraphSurf;
